import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import React, { Component } from "react";
import Context from "../../Context";
import { HTActionRequest, HTAttachment, HTDocLookup, HTRequestStatus, HTRequestType, HTRoleType } from "../../api/entities";
import { ErrorContainer } from "@eres/forms";
import { loadDocsLookUp, updateAction, validateResponse } from "../../api/api";
import { HTDocType, HTLookUps } from "../../api/constants";
import Strings from "@my-dld/assets/strings";
import PageTitle from "@my-dld/components/PageTitle";
import PageContent from "@my-dld/components/PageContent";
import { HTRequestsBCItem, MyRequestsBCItem } from "@my-dld/contexts/PageContext";
import TextInputField from "@eres/forms-web/TextInputField";
import FormRow from "@eres/forms-web/FormRow";
import { WebFormFieldProps } from "@eres/forms-web";
import StateHolder from "@eres/forms/StateHolder";
import FormAction from "@my-dld/components/Form/FormAction";
import ConfirmationPopup from "@my-dld/components/Popup/ConfirmationPopup";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { checkNonEmpty } from "@eres/ejari-web/utils";
import { RenderDocumentCategory } from "../components/Damages";
import { mapLookupWithAttachments, onDocDrop, setRequest, uploadDocs } from "../../Utils";
import SuccessPopup from "../components/SuccessPopup";
import { EjariApi } from "@eres/ejari-api";
import { JQModal } from "@my-dld/components/Popup/Utils";
import { addHTAnalytics } from "./requests/RequestDetails";

interface Props extends RoutedViewProps<{ reqNo: string }> {

}

interface State {
	isLoading: boolean;
	reason?: string;
	documents: HTDocLookup[];
	confirmReject?: boolean;
	errorContainer: ErrorContainer;
	reqNo?: string;
}

class RequestRejection extends Component<Props, State> {
	static contextType = Context;
	context!: React.ContextType<typeof Context>;

	constructor( props: Props ) {
		super( props );
		this.state = {
			isLoading: true,
			documents: [],
			errorContainer: {},
		};
	}

	componentDidMount() {
		this.loadDocuments();
	}

	componentWillUnmount() {
		JQModal( "confirm_cancel" ).hide();
		JQModal( "SuccessPopup" ).hide();
	}

	render() {
		const { request } = this.context;
		const { documents, isLoading, errorContainer, confirmReject, reqNo } = this.state;
		const filedProps: Partial<WebFormFieldProps<any, any>> = {
			holder: StateHolder.buildStateHolder( this ),
			containerClassName: "col-md-12 form-group field-style",
		};
		return <PageContent
			title={ this.getPageTitle() }
			hideQLinks={ true }>
			<PageTitle title={ Strings.request_details } paths={ [ MyRequestsBCItem, HTRequestsBCItem ] }/>
			<div className="card mb-3">
				<div className="card-header">
					<span>{ Strings.reason_for_rejection }</span>
				</div>
				<div className="card-body">
					{ isLoading && <span className="spinner-border"/> }
					{ !isLoading && <>
						<FormRow { ...filedProps }>
							<TextInputField
								required={ true }
								id={ "reason" }
								label={ Strings.reason_for_rejection }
								customInput={ <textarea rows={ 4 }/> }
								maxLength={ 500 }/>
						</FormRow>
						<FormRow>
							<RenderDocumentCategory
								documents={ documents }
								onRejected={ this.onRejected }
								onDrop={ this.onDrop }
								onDelete={ this.onDelete }
								errorContainer={ errorContainer }/>
						</FormRow>
						<FormRow>
							<FormAction
								groupClassName={ "text-center" }
								primaryActionLabel={ Strings.reject }
								onPrimaryAction={ this.isValid }
								secondaryActionLabel={ Strings.cancel }
								onSecondaryAction={ this.onCancel }/>
						</FormRow>
					</> }
				</div>
				{ confirmReject && <ConfirmationPopup
					message={ Strings.reject_request }
					onConfirm={ this.onRejectConfirm }
					onCancel={ this.onDismissReject }/> }
			</div>
			{ request && reqNo && <SuccessPopup
				actionId={ HTRequestStatus.REJECTED }
				reqType={ request.RequestTypeId }
				reqNo={ reqNo } onDismiss={ this.onDismiss }/> }
		</PageContent>;
	}

	private getPageTitle() {
		const { request } = this.context;
		if ( request ) {
			const title = request.RequestTypeId === HTRequestType.HANDOVER ? Strings.ejari_handover : Strings.ejari_takeover;
			return `${ title } - ${ Strings.request_number } : ${ request.RequestNumber }`;
		}
		return "";
	}

	private loadDocuments() {
		loadDocsLookUp( HTLookUps.ATTACHMENTS, HTDocType.REJECT ).then( docs => {
			const { request } = this.context;
			const documents = docs || [];
			if ( request && request.RejectReasonDocuments && request.RejectReasonDocuments.length > 0 ) {
				mapLookupWithAttachments( documents, request.RejectReasonDocuments );
			}
			this.setState( {
				reason: request?.RejectReason,
				documents: documents,
				isLoading: false,
			} );
		} );
	}

	private onRejected = () => {
		showErrorMessage( Strings.ht_error_total_file_size );
	};

	private onDrop = ( doc: HTDocLookup, files: File[] ) => {
		onDocDrop( doc, files, ( sDoc: HTDocLookup ) => {
			const { documents, errorContainer } = this.state;
			delete errorContainer[ "" + sDoc.Identity ];
			this.setState( {
				documents: documents.map( ( doc: HTDocLookup ) => {
					return ( sDoc.Identity === doc.Identity ) ? { ...doc, docs: sDoc.docs } : doc;
				} ),
				errorContainer: errorContainer,
			} );
		} );
	};

	private onDelete = ( attachment: HTAttachment, index: number ) => {
		if ( attachment.Identity && attachment.Identity !== 0 ) {
			showLoading();
			EjariApi.deleteTenancyContractDocument( attachment.Identity ).then( () => {
				this.updateDeleteDocs( attachment, index );
				hideLoading();
			} ).catch( reason => {
				hideLoading();
				showErrorMessage( reason );
			} );
		} else {
			this.updateDeleteDocs( attachment, index );
		}
	};

	private updateDeleteDocs( attachment: HTAttachment, index: number ) {
		const { documents } = this.state;
		this.setState( {
			documents: documents.map( ( doc: HTDocLookup ) => {
				if ( doc.Identity === attachment.DocumentId ) {
					doc.docs!.splice( index, 1 );
					return doc;
				}
				return doc;
			} ),
		}, () => {
			const { request } = this.context;
			if ( request ) {
				const attachments: HTAttachment[] = [];
				documents.forEach( doc => attachments.push( ...doc.docs || [] ) );
				request.RejectReasonDocuments = attachments;
				setRequest( request );
			}
		} );
	}

	private isValid = () => {
		const error: ErrorContainer = {};
		const { documents } = this.state;
		let isValid = checkNonEmpty( this.state, "reason", error );
		documents.forEach( ( doc: HTDocLookup ) => {
			if ( doc.IsMandatory === 1 && ( doc.docs == undefined || doc.docs.length === 0 ) ) {
				error[ "" + doc.Identity ] = Strings.required;
				isValid = false;
			}
		} );
		this.setState( {
			errorContainer: error,
		}, () => {
			if ( isValid ) {
				this.setState( { confirmReject: true } );
			}
		} );
	};

	private onDismissReject = () => {
		this.setState( {
			confirmReject: undefined,
		} );
	};

	private onRejectConfirm = () => {
		const { documents } = this.state;
		showLoading();
		uploadDocs( documents ).then( this.submitRequest ).catch( reason => {
			hideLoading();
			showErrorMessage( reason );
		} );
	};

	private submitRequest = ( attachments: HTAttachment[] ) => {
		const { request } = this.context;
		const { reason } = this.state;
		const tenant = request?.Parties.find( p => p.RoleId === HTRoleType.TENANT );
		const req: HTActionRequest = {
			ActionByTenantId: tenant?.TenantId,
			RequestStatusId: HTRequestStatus.REJECTED,
			RequestId: request?.Identity,
			RejectReason: reason,
			RejectReasonAttachments: attachments,
		};
		const reqNo = request?.RequestNumber || "";
		addHTAnalytics( "Try", HTRequestStatus.REJECTED, reqNo, request );
		updateAction( req ).then( validateResponse ).then( resp => {
			addHTAnalytics( "Success", HTRequestStatus.REJECTED, reqNo, request );
			hideLoading();
			this.setState( {
				reqNo: resp.RequestNumber,
			} );
		} ).catch( reason => {
			addHTAnalytics( "Failed", HTRequestStatus.REJECTED, reqNo, request );
			hideLoading();
			showErrorMessage( reason );
		} );
	};

	private onCancel = () => {
		this.props.history.goBack();
	};

	private onDismiss = () => {
		this.setState( {
			reqNo: undefined
		}, () => {
			this.props.history.goBack();
		} );
	};
}

export default RequestRejection;
